import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { NodeGuide } from '@app/models/entity.model';
import { BreakpointService } from '@app/services/breakpoint.service';
import { HeroTextComponent } from '@app/components/hero-text/hero-text.component';
import { MediaComponent } from '@app/components/media/media.component';
import { RelatedContentComponent } from '@app/components/related-content/related-content.component';
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { MetaInfoComponent } from '@app/components/meta-info/meta-info.component';
import { ContentLinkService } from '@app/services/contentLinks.service';
import { DOCUMENT } from '@angular/common';
import { IllustrationComponent } from '@app/components/illustration/illustration.component';
import { TextComponent } from '@app/components/text/text.component';
import { ContentLinksComponent } from '@app/components/content-links/content-links.component';
import { ParagraphLoaderComponent } from '@app/entities/paragraph/loader.component';
import { MobiLdSchemaComponent } from '@app/components/mobi-ld-schema/mobi-ld-schema.component';
import { DataService } from '@app/services/data.service';
import { RatingComponent } from '@app/components/rating/rating.component';
import { DrupalService } from '@app/services/drupal.service';
import { ApiService } from '@app/services/api.service';
import { MobiUiIconComponent } from '@mobi/rwc-ui-components-ng-jslib';

@Component({
  selector: 'mobi-node-guide',
  templateUrl: './node-guide.component.html',
  styleUrls: ['./node-guide.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    HeroTextComponent,
    MediaComponent,
    RelatedContentComponent,
    BreadcrumbComponent,
    MetaInfoComponent,
    TextComponent,
    ContentLinksComponent,
    ParagraphLoaderComponent,
    IllustrationComponent,
    MobiLdSchemaComponent,
    RatingComponent,
    MobiUiIconComponent,
  ],
})
export class NodeGuideComponent implements OnDestroy {
  readonly entity = input.required<NodeGuide>();
  breakpoint = inject(BreakpointService);

  #drupalService = inject(DrupalService);
  #data = inject(DataService);
  #doc = inject(DOCUMENT);
  #api = inject(ApiService);
  #contentLink = inject(ContentLinkService);
  #stickyEndObserver: IntersectionObserver | null = null;
  #stickyStartObserver: IntersectionObserver | null = null;

  isRatingBarDismissed = signal(false);
  isDismissible = signal(true);
  readonly startSticky = viewChild<ElementRef>('startSticky');
  readonly endSticky = viewChild<ElementRef>('endSticky');
  sticky = signal(false);

  #afterRender = afterNextRender({
    write: () => {
      this.#stickyStartObserver = new IntersectionObserver(
        ([entry]) => {
          // When startSticky enters viewport (from bottom or top), check if we should be sticky
          if (entry.isIntersecting) {
            // Start marker is visible, check if we've scrolled past it
            const startRect = entry.boundingClientRect;
            const endElement = this.endSticky()?.nativeElement;

            if (startRect.top < window.innerHeight && endElement) {
              const endRect = endElement.getBoundingClientRect();
              // If end marker is still below viewport, we should be sticky
              if (endRect.top > 0) {
                this.sticky.set(true);
              } else {
                this.sticky.set(false);
              }
            }
          } else if (
            !entry.isIntersecting &&
            entry.boundingClientRect.top > window.innerHeight
          ) {
            // Start marker is below viewport, not sticky yet
            this.sticky.set(false);
          }
        },
        {
          threshold: 0,
        }
      );

      this.#stickyEndObserver = new IntersectionObserver(
        ([entry]) => {
          // When endSticky comes into view from below, we should stop being sticky
          if (entry.isIntersecting) {
            this.sticky.set(false);
          } else if (!entry.isIntersecting && entry.boundingClientRect.top > 0) {
            // End marker is below viewport, check if start marker has entered viewport
            const startElement = this.startSticky()?.nativeElement;
            if (startElement) {
              const startRect = startElement.getBoundingClientRect();
              // If start marker is in or above viewport, we should be sticky
              if (startRect.top < window.innerHeight) {
                this.sticky.set(true);
              }
            }
          }
        },
        {
          threshold: 0,
        }
      );

      this.#stickyStartObserver.observe(this.startSticky()!.nativeElement);
      this.#stickyEndObserver.observe(this.endSticky()!.nativeElement);
    },
  });

  hasSticky = computed(() => this.#drupalService.hasSticky());

  jsonLdData = computed<any>(() => {
    let author: any;
    const authors = this.entity().authors || [];

    if (authors.length === 0) {
      // Default to organization if no authors
      author = {
        '@type': 'Organization',
        name: this.#data.orgNames[this.entity().language],
        url: this.#doc.location.origin,
      };
    } else if (authors.length === 1) {
      // Single author
      author = {
        '@type': 'Person',
        name: authors[0].label,
        url: authors[0].url.path,
      };
    } else {
      // Multiple authors
      author = authors.map((a) => ({
        '@type': 'Person',
        name: a.label,
        url: a.url.path,
      }));
    }

    const rokkaHash = this.entity().headerImage?.image?.entity?.rokka?.hash;
    const filename = this.entity().headerImage?.image?.entity?.filename;
    const rokkaUrl = this.#api.rokkaUrl;

    // Create the JSON-LD object
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: this.entity().title,
      description:
        this.entity().teaserTextShort || this.entity().teaserText || this.entity().lead,
      image: rokkaUrl.concat(rokkaHash!, '/', filename || ''),
      datePublished: this.entity().created,
      dateModified: this.entity().changed,
      author,
    };
  });

  contentLinks = computed(() => {
    const paragraphs = this.entity()?.paragraphs;
    return this.#contentLink.getContentLinks(paragraphs);
  });

  formattedLastUpdated = computed(() => {
    const lastUpdatedValue = this.entity().changed;
    if (lastUpdatedValue) {
      const date = new Date(lastUpdatedValue);
      return date.toLocaleString(this.entity().language, {
        month: 'long',
        year: 'numeric',
      });
    }
    return '';
  });

  ngOnDestroy() {
    this.#stickyStartObserver?.disconnect();
    this.#stickyEndObserver?.disconnect();
  }
}
